import { gql } from "@apollo/client";
import {
  Button,
  Card,
  Dialog,
  Divider,
  Fab,
  TextField,
  Typography,
  useTheme,
} from "@material-ui/core";
import { Icon } from "@resource/atlas";
import { atlasPlus } from "@resource/atlas/icons";
import DialogTitle from "components/legacy/Dialog/DialogTitle/DialogTitle";
import {
  CreateValue,
  CreateValueVariables,
  DeleteValue,
  DeleteValueVariables,
  OrganizationValue,
  UpdateValue,
  UpdateValueVariables,
} from "generated/schemaTypes";
import Cookies from "js-cookie";
import { useState } from "react";
import useAnalytics from "react-hooks/useAnalytics";
import { ORGANIZATION_ID_COOKIE_NAME } from "shared/constants/auth";
import useMutation from "utils/useMutation";

import styles from "./Values.module.scss";

const ORGANIZATION_VALUE_FRAGMENT = gql`
  fragment OrganizationValue on Value {
    id
    title
    description
  }
`;

const CREATE_VALUE = gql`
  mutation CreateValue($input: CreateValueMutationInput!) {
    createValue(input: $input) {
      success
      code
      message
    }
  }
`;

const UPDATE_VALUE = gql`
  mutation UpdateValue($input: UpdateValueMutationInput!) {
    updateValue(input: $input) {
      success
      code
      message
      value {
        id
        title
        description
      }
    }
  }
`;

const DELETE_VALUE = gql`
  mutation DeleteValue($input: DeleteValueMutationInput!) {
    deleteValue(input: $input) {
      success
      code
      message
    }
  }
`;

type ValuesProps = {
  values: OrganizationValue[];
  disabled?: boolean;
};

function Values({ values, disabled }: ValuesProps) {
  const analytics = useAnalytics();
  const theme = useTheme();
  const currentOrganizationId = Cookies.get(ORGANIZATION_ID_COOKIE_NAME);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState({
    id: "",
    title: "",
    description: "",
  });
  const [createValue] = useMutation<CreateValue, CreateValueVariables>(
    CREATE_VALUE,
    {
      refetchQueries: ["GlobalOrganizationContentForCompanySettings"],
    }
  );
  const [updateValue] = useMutation<UpdateValue, UpdateValueVariables>(
    UPDATE_VALUE
  );
  const [deleteValue] = useMutation<DeleteValue, DeleteValueVariables>(
    DELETE_VALUE,
    {
      refetchQueries: ["GlobalOrganizationContentForCompanySettings"],
    }
  );

  const handleValueCardClick = ({
    id,
    title,
    description,
  }: {
    id: string;
    title: string;
    description: string | null;
  }): void => {
    if (analytics) {
      analytics.track("Company Page Value Card Clicked");
    }
    setValue({
      id,
      title,
      description: description || "",
    });
    setOpen(true);
  };

  const handleAddValueClick = (): void => {
    setValue({
      id: "",
      title: "New Value",
      description: "",
    });
    setOpen(true);
  };

  const handleAddValue = async (): Promise<void> => {
    if (!currentOrganizationId) {
      return;
    }
    createValue({
      variables: {
        input: {
          title: value.title,
          description: value.description,
          organizationId: currentOrganizationId,
        },
      },
    });
    setOpen(false);
    if (analytics) {
      analytics.track("Company Page Value Card Created");
    }
  };

  const handleDeleteValue = async (): Promise<void> => {
    if (value.id) {
      deleteValue({
        variables: {
          input: {
            valueId: value.id,
          },
        },
      });
      setOpen(false);
      if (analytics) {
        analytics.track("Company Page Value Card Deleted");
      }
    }
  };

  const handleUpdateValue = async (): Promise<void> => {
    if (value.id && value.title) {
      updateValue({
        variables: {
          input: {
            valueId: value.id,
            title: value.title,
            description: value.description,
          },
        },
      });
      setOpen(false);
      if (analytics) {
        analytics.track("Company Page Value Card Updated");
      }
    }
  };

  return (
    <div className={styles.valuesContainer}>
      <div className={styles.headerCont}>
        <Typography className={styles.text} variant="h3">
          <span role="img" aria-label="Pray Hands">
            🙏
          </span>{" "}
          Values
        </Typography>
        <Fab
          disabled={disabled}
          onClick={(): void => {
            if (disabled) return;
            handleAddValueClick();
          }}
          color="primary"
          size="small"
          aria-label="add value"
        >
          <Icon content={atlasPlus} className="w-6 h-6" />
        </Fab>
      </div>
      <div className={styles.valueCardsCont}>
        {values.map(({ id, title, description }) => (
          <Card
            key={id}
            className={styles.valueCard}
            style={{ cursor: disabled ? "default" : "pointer" }}
            onClick={(): void => {
              if (disabled) return;
              handleValueCardClick({
                id,
                title,
                description,
              });
            }}
          >
            <Typography className={styles.headerText} variant="h4">
              {title}
            </Typography>
            <Typography className={styles.description} variant="body1">
              {description}
            </Typography>
          </Card>
        ))}
      </div>
      <Dialog open={open} fullWidth onClose={(): void => setOpen(false)}>
        <DialogTitle onClose={(): void => setOpen(false)}>
          {value.id ? "Update a Value" : "Create a Value"}
        </DialogTitle>
        <Divider />
        <div className={styles.dialogCont}>
          <TextField
            label="Title"
            variant="outlined"
            placeholder="Transparency"
            value={value.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { value: title } = e.target;
              setValue((prevValue) => ({
                ...prevValue,
                title,
              }));
            }}
            fullWidth
          />
          <TextField
            variant="outlined"
            placeholder="Why is this your value? Attract like minded candidates who share your values."
            value={value.description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { value: description } = e.target;
              setValue((prevValue) => ({
                ...prevValue,
                description,
              }));
            }}
            multiline
            fullWidth
          />
          <div className={styles.buttonContainer}>
            {value.id && (
              <Button
                variant="outlined"
                size="large"
                style={{
                  color: theme.palette.error.main,
                }}
                onClick={(): void => {
                  handleDeleteValue();
                }}
              >
                Delete
              </Button>
            )}
            <Button
              disabled={!value.title}
              variant="contained"
              color="primary"
              size="large"
              onClick={(): void => {
                if (value.id) {
                  handleUpdateValue();
                } else {
                  handleAddValue();
                }
              }}
            >
              {value.id ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

Values.fragments = {
  organizationValue: ORGANIZATION_VALUE_FRAGMENT,
};

export default Values;
